/**
 * Accommodation — the shared vocabulary for hotels, motels, guest houses,
 * lodges and serviced apartments on the platform.
 *
 * Accommodation is NOT a fifth storefront domain: a hotel keeps a host
 * industry (electronics = kuttostechnologies.com) and has the business kind
 * "accommodation" (lib/config/businessKinds.ts). What changes is the product
 * template (a ROOM TYPE: nightly and hourly rates, sleeps, bed, amenities),
 * the operations (units, reservations, check-in / check-out, housekeeping),
 * a bill that grows through the stay, and the wording.
 *
 * Calendar days are integers in the brand's local day — 20260912 — never
 * epoch milliseconds. Hotels think in nights, not timestamps, and the
 * bookings calendar already buckets in EAT (UTC+3); an epoch at midnight UTC
 * is the previous day in Kampala, which is exactly the bug this avoids.
 *
 * Plain TypeScript (one type import) — imported by the React app and Convex.
 */

import type { BusinessKind } from "./businessKinds";

export const ACCOMMODATION_INDUSTRY_SLUG = "accommodation" as const;
export const ACCOMMODATION_KIND: BusinessKind = "accommodation";

/** Local-time offset the platform runs on (East Africa Time, no DST). */
export const LOCAL_OFFSET_MINUTES = 180;
export const DAY_MS = 24 * 60 * 60 * 1000;

export type RateMode = "nightly" | "hourly" | "both";
export type StayRateMode = "nightly" | "hourly";
export type StayStatus = "reserved" | "checked_in" | "checked_out" | "no_show" | "cancelled";
export type HousekeepingStatus = "clean" | "dirty" | "cleaning" | "inspected";
export type UnitStatus = "active" | "out_of_order";

export interface StayDetails {
  rateMode: RateMode;
  hourlyRate?: number;
  /** Minutes per hourly block, e.g. 180 for "3 hours". */
  hourlyBlockMinutes?: number;
  /** Friday and Saturday nights, when different. */
  weekendRate?: number;
  maxAdults: number;
  maxChildren?: number;
  bedType?: string;
  sizeSqm?: number;
  amenities?: string[];
  /** Override the brand's default check-in / check-out for this type. */
  checkInTime?: string;
  checkOutTime?: string;
  minNights?: number;
  extraGuestFee?: number;
  /** false = taken off sale without deleting it. */
  available?: boolean;
}

export interface AccommodationSettings {
  checkInTime?: string;
  checkOutTime?: string;
  depositRule?: { type: "nights" | "percent" | "none"; value?: number };
  /** How long an unpaid online hold keeps its nights. */
  holdMinutes?: number;
  requireIdAtCheckIn?: boolean;
  cancellationPolicy?: string;
  hourlyEnabled?: boolean;
  /** The municipality's levy on guests received — see lhtForStay(). */
  localHotelTax?: LocalHotelTax;
  /** The refundable deposit held against damage or use — see securityDepositFor(). */
  securityDeposit?: SecurityDepositRule;
  /** What is placed in a room for the guest: the check-out inspection list. */
  inRoomItems?: InRoomItem[];
}

/**
 * Local hotel tax (LHT). Ugandan municipalities levy it on guests received —
 * some per room-night or per stay (added to the guest's bill, or absorbed by
 * the hotel), some as a fixed monthly figure by room count that the hotel pays.
 */
export type LocalHotelTaxMode = "none" | "per_night" | "per_stay" | "monthly_fixed";
export interface LocalHotelTax {
  mode: LocalHotelTaxMode;
  /** UGX per room-night, per stay, or per month — by mode. */
  amount?: number;
  /** Who bears it. A monthly figure is always the hotel's. */
  paidBy?: "guest" | "hotel";
  /** How the line reads on the bill. */
  label?: string;
}

/** A refundable deposit against damage or use. Held money, never revenue. */
export type SecurityDepositMode = "none" | "percent" | "fixed";
export interface SecurityDepositRule {
  mode: SecurityDepositMode;
  /** Percent of the room total, or a fixed UGX figure — by mode. */
  value?: number;
  /** Ask on every stay, or only on stays by the night (not day use). */
  askFor?: "all" | "nightly_only";
}

/** Something placed in the room the guest may use — water, a soda, tea. */
export interface InRoomItem {
  id: string;
  name: string;
  /** What using one adds to the bill (0 = complimentary, still counted). */
  price: number;
  /** The stock item, when the brand tracks it — using one takes the stock. */
  productId?: string;
  /** The standard number placed in the room. */
  placed?: number;
}

export const DEFAULT_CHECK_IN = "14:00";
export const DEFAULT_CHECK_OUT = "10:00";
export const DEFAULT_HOLD_MINUTES = 120;
export const DEFAULT_DEPOSIT_RULE: NonNullable<AccommodationSettings["depositRule"]> = { type: "nights", value: 1 };

export const STAY_FLOW: ReadonlyArray<{ value: StayStatus; label: string; tone: string }> = [
  { value: "reserved", label: "Reserved", tone: "info" },
  { value: "checked_in", label: "Checked in", tone: "good" },
  { value: "checked_out", label: "Checked out", tone: "muted" },
  { value: "no_show", label: "No show", tone: "warn" },
  { value: "cancelled", label: "Cancelled", tone: "danger" },
];

export const HOUSEKEEPING_FLOW: ReadonlyArray<{
  value: HousekeepingStatus;
  label: string;
  next?: HousekeepingStatus;
  action?: string;
  tone: string;
}> = [
  { value: "dirty", label: "Dirty", next: "cleaning", action: "Start cleaning", tone: "danger" },
  { value: "cleaning", label: "Cleaning", next: "inspected", action: "Mark inspected", tone: "warn" },
  { value: "inspected", label: "Inspected", next: "clean", action: "Ready for guests", tone: "info" },
  { value: "clean", label: "Ready", tone: "good" },
];

export const BED_TYPES: ReadonlyArray<string> = [
  "Single", "Double", "Queen", "King", "Twin", "Bunk beds", "Dormitory bed",
];

export const AMENITIES: ReadonlyArray<string> = [
  "Wi-Fi", "Air conditioning", "Fan", "TV", "Hot shower", "Ensuite bathroom", "Shared bathroom",
  "Breakfast included", "Mosquito net", "Balcony", "Mini fridge", "Work desk", "Wardrobe",
  "Room service", "Kettle", "Safe", "Parking", "Lake view", "Garden view",
];

export const ID_DOCUMENT_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "national_id", label: "National ID" },
  { value: "passport", label: "Passport" },
  { value: "driving_license", label: "Driving permit" },
  { value: "other", label: "Other" },
];

export const ROOM_PLACEHOLDER_IMAGE = "/images/placeholder-product.jpg";

/**
 * Platform-level categories for the industry (categories are per industry,
 * not per brand). Seeded once by `accommodation/seed:seedRoomCategories`.
 */
export const ROOM_CATEGORY_SEED: ReadonlyArray<{ name: string; slug: string; icon: string; description: string }> = [
  { name: "Standard Room", slug: "standard-room", icon: "🛏️", description: "The everyday room — one bed, the essentials" },
  { name: "Deluxe Room", slug: "deluxe-room", icon: "✨", description: "More space, a better bed, the extras" },
  { name: "Twin Room", slug: "twin-room", icon: "🛏️🛏️", description: "Two single beds" },
  { name: "Family Room", slug: "family-room", icon: "👨‍👩‍👧", description: "Sleeps a family — more beds or a connecting room" },
  { name: "Suite", slug: "suite", icon: "🏨", description: "A bedroom with its own sitting area" },
  { name: "Dormitory Bed", slug: "dormitory-bed", icon: "🎒", description: "One bed in a shared room, priced per bed" },
  { name: "Self-catering Unit", slug: "self-catering-unit", icon: "🍳", description: "An apartment or cottage with its own kitchen" },
  { name: "Conference Room", slug: "conference-room", icon: "🪑", description: "Meeting space, by the hour or the day" },
];

// ── Predicates ───────────────────────────────────────────────────────────

/** A product that is a room type. */
export function isRoomProduct(product: any): boolean {
  if (!product) return false;
  return product.kind === "room" || !!product.stay;
}

/** A product that belongs on the retail Products page, till grid and shop grid. */
export function isRetailProduct(product: any): boolean {
  const kind = product?.kind;
  return !kind || kind === "product";
}

// ── Calendar days (YYYYMMDD integers) ────────────────────────────────────

export function dayIntFromISO(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) throw new Error(`Not a date: ${iso}`);
  return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
}

export function isoFromDayInt(day: number): string {
  const y = Math.floor(day / 10000);
  const mo = Math.floor((day % 10000) / 100);
  const d = day % 100;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function isValidDayInt(day: number): boolean {
  if (!Number.isInteger(day)) return false;
  const y = Math.floor(day / 10000), mo = Math.floor((day % 10000) / 100), d = day % 100;
  if (y < 2000 || y > 2100 || mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d;
}

/** Midnight UTC of the calendar day — only for arithmetic, never for display. */
export function dayIntToUtcMs(day: number): number {
  return Date.UTC(Math.floor(day / 10000), Math.floor((day % 10000) / 100) - 1, day % 100);
}

export function dayIntFromUtcMs(ms: number): number {
  const t = new Date(ms);
  return t.getUTCFullYear() * 10000 + (t.getUTCMonth() + 1) * 100 + t.getUTCDate();
}

export function addDays(day: number, n: number): number {
  return dayIntFromUtcMs(dayIntToUtcMs(day) + n * DAY_MS);
}

/** Whole days from a to b (b exclusive). Negative when b is before a. */
export function daysBetween(a: number, b: number): number {
  return Math.round((dayIntToUtcMs(b) - dayIntToUtcMs(a)) / DAY_MS);
}

/** The calendar day it is now, in the brand's local time. */
export function todayDayInt(nowMs: number = Date.now(), offsetMinutes: number = LOCAL_OFFSET_MINUTES): number {
  return dayIntFromUtcMs(nowMs + offsetMinutes * 60 * 1000);
}

/** Minutes since local midnight, now. */
export function nowLocalMinutes(nowMs: number = Date.now(), offsetMinutes: number = LOCAL_OFFSET_MINUTES): number {
  const local = new Date(nowMs + offsetMinutes * 60 * 1000);
  return local.getUTCHours() * 60 + local.getUTCMinutes();
}

/** Nights of a stay: every day from check-in up to (not including) check-out. */
export function eachNight(checkIn: number, checkOut: number): number[] {
  const n = daysBetween(checkIn, checkOut);
  if (n <= 0) return [];
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(addDays(checkIn, i));
  return out;
}

/** 0 = Sunday … 6 = Saturday, for the calendar day. */
export function weekday(day: number): number {
  return new Date(dayIntToUtcMs(day)).getUTCDay();
}

/** A "weekend night" is the night of Friday or Saturday. */
export function isWeekendNight(day: number): boolean {
  const w = weekday(day);
  return w === 5 || w === 6;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "12 Sep" / "12 Sep 2026" */
export function formatDayInt(day: number, withYear = false): string {
  const y = Math.floor(day / 10000), mo = Math.floor((day % 10000) / 100), d = day % 100;
  return `${d} ${MONTHS[mo - 1] ?? "?"}${withYear ? ` ${y}` : ""}`;
}

/** "Fri 12 Sep" */
export function formatDayIntLong(day: number): string {
  return `${WEEKDAYS[weekday(day)]} ${formatDayInt(day)}`;
}

/** "12–15 Sep" or "30 Sep – 2 Oct" */
export function formatDayRange(checkIn: number, checkOut: number): string {
  const sameMonth = Math.floor(checkIn / 100) === Math.floor(checkOut / 100);
  if (sameMonth) return `${checkIn % 100}–${formatDayInt(checkOut)}`;
  return `${formatDayInt(checkIn)} – ${formatDayInt(checkOut)}`;
}

// ── Time of day ("14:00") ────────────────────────────────────────────────

export function minutesFromClock(clock: string | undefined, fallback: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec((clock || fallback).trim());
  if (!m) return minutesFromClock(fallback, DEFAULT_CHECK_IN);
  return Math.min(23 * 60 + 59, Math.max(0, Number(m[1]) * 60 + Number(m[2])));
}

export function clockFromMinutes(minutes: number): string {
  const m = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** Half-hour slot index within a day (0–47) that a minute falls in. */
export const SLOT_MINUTES = 30;
export function slotOfMinute(minute: number): number {
  return Math.floor(Math.max(0, Math.min(24 * 60 - 1, minute)) / SLOT_MINUTES);
}
/** Slots [start, end) an hourly stay occupies on its day. */
export function slotsBetween(startMinutes: number, endMinutes: number): number[] {
  const first = slotOfMinute(startMinutes);
  const last = Math.ceil(Math.min(24 * 60, Math.max(startMinutes + 1, endMinutes)) / SLOT_MINUTES);
  const out: number[] = [];
  for (let s = first; s < last; s++) out.push(s);
  return out;
}

// ── Pricing ──────────────────────────────────────────────────────────────

export interface RoomPricing {
  nightlyRate: number;
  weekendRate?: number;
  hourlyRate?: number;
  hourlyBlockMinutes?: number;
  extraGuestFee?: number;
  maxAdults?: number;
}

/** Price of a nightly stay, night by night, with the weekend rate where it applies. */
export function nightlyStayTotal(pricing: RoomPricing, nights: number[], adults = 1): { total: number; perNight: number[] } {
  const perNight = nights.map((n) =>
    pricing.weekendRate && isWeekendNight(n) ? pricing.weekendRate : pricing.nightlyRate,
  );
  const extra =
    pricing.extraGuestFee && pricing.maxAdults && adults > pricing.maxAdults
      ? (adults - pricing.maxAdults) * pricing.extraGuestFee * nights.length
      : 0;
  return { total: perNight.reduce((a, b) => a + b, 0) + extra, perNight };
}

/** Price of an hourly stay: whole blocks, rounded up. */
export function hourlyStayTotal(pricing: RoomPricing, startMinutes: number, endMinutes: number): { total: number; blocks: number } {
  const block = Math.max(30, pricing.hourlyBlockMinutes || 60);
  const minutes = Math.max(1, endMinutes - startMinutes);
  const blocks = Math.ceil(minutes / block);
  return { total: blocks * (pricing.hourlyRate || 0), blocks };
}

/** What the brand's deposit rule asks for, capped at the total. */
export function depositDue(rule: AccommodationSettings["depositRule"] | undefined, roomTotal: number, nightlyRate: number): number {
  const r = rule || DEFAULT_DEPOSIT_RULE;
  if (r.type === "none") return 0;
  if (r.type === "percent") return Math.min(roomTotal, Math.round((roomTotal * (r.value ?? 30)) / 100));
  return Math.min(roomTotal, Math.round(nightlyRate * (r.value ?? 1)));
}

export const DEFAULT_LHT_LABEL = "Local hotel tax";

/**
 * What the municipality's levy comes to for ONE stay. Null when nothing is
 * charged per stay (no rule, or a monthly figure the hotel pays on its own).
 * A per-night levy does not touch day use; a per-stay levy counts it.
 */
export function lhtForStay(
  rule: LocalHotelTax | undefined,
  stay: { rateMode: string; nights: number },
): { amount: number; perUnit: number; units: number; mode: LocalHotelTaxMode; paidBy: "guest" | "hotel"; label: string } | null {
  if (!rule || rule.mode === "none" || rule.mode === "monthly_fixed") return null;
  const perUnit = Math.max(0, Math.round(Number(rule.amount) || 0));
  if (perUnit <= 0) return null;
  const units = rule.mode === "per_night" ? (stay.rateMode === "hourly" ? 0 : Math.max(0, Math.round(stay.nights))) : 1;
  if (units <= 0) return null;
  return {
    amount: perUnit * units,
    perUnit,
    units,
    mode: rule.mode,
    paidBy: rule.paidBy === "hotel" ? "hotel" : "guest",
    label: (rule.label || "").trim() || DEFAULT_LHT_LABEL,
  };
}

/** The refundable deposit the house rule asks for on a stay (0 = none). */
export function securityDepositFor(rule: SecurityDepositRule | undefined, roomTotal: number, rateMode: string): number {
  if (!rule || rule.mode === "none") return 0;
  if (rule.askFor === "nightly_only" && rateMode === "hourly") return 0;
  const value = Math.max(0, Number(rule.value) || 0);
  if (value <= 0) return 0;
  if (rule.mode === "percent") return Math.round((roomTotal * Math.min(100, value)) / 100);
  return Math.round(value);
}

/** "3 nights" / "1 night" / "4 hours" */
export function stayLengthLabel(stay: { rateMode: string; nights?: number; startMinutes?: number; endMinutes?: number }): string {
  if (stay.rateMode === "hourly") {
    const mins = Math.max(0, (stay.endMinutes ?? 0) - (stay.startMinutes ?? 0));
    const h = Math.round((mins / 60) * 10) / 10;
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  const n = stay.nights ?? 0;
  return `${n} night${n === 1 ? "" : "s"}`;
}
